import getDOMfromString from '../../helpers/getDOMfromString';
import {gsap} from '../../vendors/gsap';
import {Flip} from '../../vendors/gsap/Flip';
import {setMobileMenuHeight} from '../menu/index';

/**
 * Image shrink animation from explore/timeline
 *
 * @param {Object} config - params object
 * @param {HTMLElement} [config.triggerContainer] - element that was clicked to open individual records view
 * @param {string} [config.viewMode] - view from which view is triggered (explore/timeline)
 * @returns {undefined}
 */
export const shrinkImageAnimation = ({triggerContainer, viewMode}) => {
    if (!triggerContainer) return;

    setMobileMenuHeight('tall');

    // get active slide
    const activeContainer = document.querySelector('.main-carousel .swiper-slide-active');

    // clone active image and hide it
    const node = activeContainer.querySelector('img');
    const clone = node.cloneNode(true);
    clone.classList.add('hidden');
    clone.classList.add('zoom-image');

    // append to active container
    activeContainer.append(clone);

    // set height of container and hide image in trigger container
    const selector = viewMode === 'explore' ? 'a' : 'a .asset';
    const imageContainer = triggerContainer.querySelector(selector);
    const triggerImage = triggerContainer.querySelector('img');
    gsap.set(imageContainer, {
        height: gsap.getProperty(imageContainer, 'height'),
        width: gsap.getProperty(imageContainer, 'width')
    });
    triggerImage.classList.add('hidden');

    // adding z index to put animating image at the top
    let triggerSlide = viewMode === 'timeline' ? triggerContainer.closest('.swiper-slide') : triggerContainer;
    triggerSlide.classList.add('z-50');

    // adding div to place 2 images and animate, if active slide is not equal to initial slide
    let animateNode = node;

    const activeIndex = activeContainer.dataset.swiperSlideIndex;
    if (activeIndex !== '0') {
        const firstImage = document.querySelector('.main-carousel .swiper-slide[data-swiper-slide-index="0"] img');
        const div = getDOMfromString(`<div class="relative block h-full mx-auto lg:m-0 overflow-hidden"></div>`);
        div.style.maxWidth = `${gsap.getProperty(node, 'width')}px`;
        activeContainer.append(div);
        firstImage.classList.add('absolute', 'left-0', 'top-0', 'w-full', 'h-full', 'object-cover', 'object-left-top');
        node.classList.add('h-full');
        div.append(firstImage);
        div.append(node);
        animateNode = div;
        gsap.to(node, {autoAlpha: 0, duration: 0.7, ease: 'power1.in'});
    }

    // explore view has opacity on image container.
    // For smooth image animation, temporarily add opacity 1 while animating
    imageContainer.classList.add('!opacity-100');
    if (imageContainer.style.opacity) {
        gsap.fromTo(node, {opacity: 1}, {opacity: imageContainer.style.opacity, delay: 0.1, ease: 'power1.in'});
    }

    const state = Flip.getState(animateNode);

    // move to expanded image
    imageContainer.prepend(animateNode);

    Flip.from(state, {
        duration: 0.7,
        ease: 'power2.inOut',
        scale: true,
        /**
         * oncomplete callback
         */
        onComplete() {
            animateNode.remove();
            setTimeout(() => clone.classList.remove('hidden'), 300);
            triggerImage.classList.remove('hidden');
            imageContainer.style.width = 'auto';
            imageContainer.style.height = 'auto';
            triggerSlide.classList.remove('z-50');
            imageContainer.classList.remove('!opacity-100');
        }
    });
};

/**
 * Image expand animation from explore/timeline
 *
 * @param {Object} config - params object
 * @param {HTMLElement} [config.triggerContainer] - element that was clicked to open individual records view
 * @param {string} [config.viewMode] - view from which view is triggered (explore/timeline)
 * @returns {undefined}
 */
export const expandImageAnimation = ({triggerContainer, viewMode}) => {
    if (!triggerContainer) return;

    // clone active image and hide it in trigger view
    const node = triggerContainer.querySelector('img');
    const clone = node.cloneNode(true);

    // set height of container
    const selector = viewMode === 'explore' ? 'a' : 'a .asset';
    const imageContainer = triggerContainer.querySelector(selector);
    gsap.set(imageContainer, {
        height: gsap.getProperty(node, 'height'),
        width: gsap.getProperty(node, 'width')
    });

    // append cloned image to active container in zoom carousel
    imageContainer.append(clone);

    // add classes and hide active image in main carousel
    const mainCarouselContainer = document.querySelector('.main-carousel');
    mainCarouselContainer.classList.add('hide-prev'); // hide prev slides so that overflow visible will not show it
    const mainCarouselSwiper = document.querySelector('.main-carousel .swiper');
    const mainActiveSlideContainer = mainCarouselContainer.querySelector('.swiper-slide');
    mainCarouselContainer.classList.add('!overflow-visible', 'w-full');
    mainCarouselSwiper.classList.add('!overflow-visible', 'w-full');
    const mainActiveSlideImage = mainActiveSlideContainer.querySelector('img');
    mainActiveSlideImage.classList.add('hidden');

    // animate
    const state = Flip.getState(node);

    // move image from expanded view to main view
    mainActiveSlideContainer.append(node);

    Flip.from(state, {
        duration: 0.7,
        ease: 'power2.inOut',
        absolute: true,
        scale: true,
        /**
         * oncomplete callback
         */
        onComplete() {
            setTimeout(() => {
                node.remove();
            }, 700);
            mainActiveSlideImage.classList.remove('hidden');
            mainCarouselContainer.classList.remove('!overflow-visible', 'w-full');
            mainCarouselSwiper.classList.remove('!overflow-visible', 'w-full');
            mainCarouselContainer.classList.remove('hide-prev');
            imageContainer.style.width = 'auto';
            imageContainer.style.height = 'auto';
        }
    });
};
